import { LayerType } from "@blings/blings-player";
import type { TextAnimationPhase } from "./keyframeHelpers";
import { TEXT_ANIMATION_PHASES } from "./keyframeHelpers";
import type { TimelineMotionPhase } from "./animationPresetHelpers";
import { TIMELINE_MOTION_PHASES } from "./animationPresetHelpers";
import type { TimelineLayerNode } from "./layerHierarchy";
import { normalizeAssetId } from "./timingModHelpers";
import { getResolvedLayerTiming } from "../TimelineTrack";
import {
  collapseAnimatedPropertiesToStaticValue,
  getValueAtObjectPath,
} from "./rootAnimationModHelpers";

type TextAnimationPhaseSegment = {
  startFrame: number;
  endFrame: number;
};

type TextAnimationPhaseTiming = {
  enter?: TextAnimationPhaseSegment;
  exit?: TextAnimationPhaseSegment;
};

type TextAnimationSelectorTiming = {
  startFrame: number;
  endFrame: number;
  exitTiming?: {
    startFrame: number;
    endFrame: number;
  };
};

const TEXT_ANIMATION_PHASE_LABELS: Record<TextAnimationPhase, string> = {
  in: "In",
  out: "Out",
  both: "Both",
};

const TEXT_PRESET_MENU_KEY_PREFIX = "text-preset:";
const MOTION_PRESET_MENU_KEY_PREFIX = "motion-preset:";
const TEXT_STYLE_MENU_KEY_PREFIX = "text-style:";
const LAYER_STYLE_MENU_KEY_PREFIX = "layer-style:";
const DEFAULT_TEXT_ANIMATION_DURATION_FRAMES = 100;
const UNSUPPORTED_TIMELINE_ANIMATION_LAYER_TYPES = new Set<number>([
  LayerType.audio,
  LayerType.video,
  LayerType.pholderVideo,
]);

type TextStyleAnimationProperty =
  | "bl"
  | "fh"
  | "fs"
  | "fb"
  | "fc"
  | "fo"
  | "sh"
  | "ss"
  | "sb"
  | "sc"
  | "sw"
  | "so";

type TextStyleAnimationConfig = {
  propertyKey: TextStyleAnimationProperty;
  label: string;
  animatorName: string;
};

type LayerStyleAnimationProperty =
  | "blur"
  | "hue"
  | "saturation"
  | "brightness"
  | "fillColor"
  | "fillOpacity"
  | "strokeColor"
  | "strokeWidth"
  | "strokeOpacity";

type ExistingLayerStyleAnimationTarget = {
  kind: "existing";
  propertyKey: Exclude<LayerStyleAnimationProperty, "blur">;
  path: string;
};

type SolidFillColorLayerStyleAnimationTarget = {
  kind: "solidFillColor";
  propertyKey: "fillColor";
  color: string;
};

type LayerStyleAnimationTarget =
  | ExistingLayerStyleAnimationTarget
  | SolidFillColorLayerStyleAnimationTarget;

type TimelineMotionPresetDirection = "left" | "right" | "up" | "down";

const TIMELINE_MOTION_PRESET_DIRECTIONS: readonly TimelineMotionPresetDirection[] = [
  "left",
  "right",
  "up",
  "down",
] as const;

const TEXT_STYLE_ANIMATION_CONFIGS: Record<
  TextStyleAnimationProperty,
  TextStyleAnimationConfig
> = {
  bl: {
    propertyKey: "bl",
    label: "Blur",
    animatorName: "Blur",
  },
  fh: {
    propertyKey: "fh",
    label: "Fill Hue",
    animatorName: "Fill Hue",
  },
  fs: {
    propertyKey: "fs",
    label: "Fill Saturation",
    animatorName: "Fill Saturation",
  },
  fb: {
    propertyKey: "fb",
    label: "Fill Brightness",
    animatorName: "Fill Brightness",
  },
  fc: {
    propertyKey: "fc",
    label: "Fill Color",
    animatorName: "Fill Color",
  },
  fo: {
    propertyKey: "fo",
    label: "Fill Opacity",
    animatorName: "Fill Opacity",
  },
  sh: {
    propertyKey: "sh",
    label: "Stroke Hue",
    animatorName: "Stroke Hue",
  },
  ss: {
    propertyKey: "ss",
    label: "Stroke Saturation",
    animatorName: "Stroke Saturation",
  },
  sb: {
    propertyKey: "sb",
    label: "Stroke Brightness",
    animatorName: "Stroke Brightness",
  },
  sc: {
    propertyKey: "sc",
    label: "Stroke Color",
    animatorName: "Stroke Color",
  },
  sw: {
    propertyKey: "sw",
    label: "Stroke Width",
    animatorName: "Stroke Width",
  },
  so: {
    propertyKey: "so",
    label: "Stroke Opacity",
    animatorName: "Stroke Opacity",
  },
};

const LAYER_STYLE_MENU_LABELS: Record<LayerStyleAnimationProperty, string> = {
  blur: "Blur",
  hue: "Hue",
  saturation: "Saturation",
  brightness: "Brightness",
  fillColor: "Fill Color",
  fillOpacity: "Fill Opacity",
  strokeColor: "Stroke Color",
  strokeWidth: "Stroke Width",
  strokeOpacity: "Stroke Opacity",
};

const NAMED_LAYER_STYLE_PROPERTIES: Record<
  string,
  Exclude<LayerStyleAnimationProperty, "blur">
> = {
  hue: "hue",
  saturation: "saturation",
  brightness: "brightness",
  "fill color": "fillColor",
  "fill opacity": "fillOpacity",
  "stroke color": "strokeColor",
  "stroke width": "strokeWidth",
  "stroke opacity": "strokeOpacity",
};

function getTimelineMotionTarget(layer: TimelineLayerNode["layer"]) {
  return {
    assetId: normalizeAssetId(layer.assetId),
    layerName: layer.nm,
    layerUid: layer.uid,
  };
}

function supportsTimelineAnimation(layerType?: number): boolean {
  return !UNSUPPORTED_TIMELINE_ANIMATION_LAYER_TYPES.has(layerType ?? -1);
}

function getStaticNumberValue(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (
    value &&
    typeof value === "object" &&
    "a" in (value as Record<string, unknown>) &&
    "k" in (value as Record<string, unknown>) &&
    (value as { a?: number }).a === 0 &&
    typeof (value as { k?: unknown }).k === "number"
  ) {
    return (value as { k: number }).k;
  }

  return undefined;
}

function buildTextPresetMenuKey(
  presetName: string,
  phase: TextAnimationPhase
): string {
  return `${TEXT_PRESET_MENU_KEY_PREFIX}${presetName}:${phase}`;
}

function buildMotionPresetMenuKey(
  presetName: string,
  phase: TimelineMotionPhase,
  direction?: TimelineMotionPresetDirection
): string {
  const directionSegment = direction ? `:${direction}` : "";
  return `${MOTION_PRESET_MENU_KEY_PREFIX}${presetName}${directionSegment}:${phase}`;
}

function parseTextPresetMenuKey(
  key: string
): { presetName: string; phase: TextAnimationPhase } | null {
  if (!key.startsWith(TEXT_PRESET_MENU_KEY_PREFIX)) {
    return null;
  }

  const payload = key.slice(TEXT_PRESET_MENU_KEY_PREFIX.length);
  const lastSeparatorIndex = payload.lastIndexOf(":");
  if (lastSeparatorIndex === -1) {
    return null;
  }

  const presetName = payload.slice(0, lastSeparatorIndex);
  const phase = payload.slice(lastSeparatorIndex + 1) as TextAnimationPhase;
  if (!TEXT_ANIMATION_PHASES.includes(phase)) {
    return null;
  }

  return { presetName, phase };
}

function parseMotionPresetMenuKey(
  key: string
): {
  presetName: string;
  phase: TimelineMotionPhase;
  direction?: TimelineMotionPresetDirection;
} | null {
  if (!key.startsWith(MOTION_PRESET_MENU_KEY_PREFIX)) {
    return null;
  }

  const payload = key.slice(MOTION_PRESET_MENU_KEY_PREFIX.length);
  const lastSeparatorIndex = payload.lastIndexOf(":");
  if (lastSeparatorIndex === -1) {
    return null;
  }

  const presetName = payload.slice(0, lastSeparatorIndex);
  const phase = payload.slice(lastSeparatorIndex + 1) as TimelineMotionPhase;
  if (!TIMELINE_MOTION_PHASES.includes(phase)) {
    return null;
  }

  const presetAndDirection = payload.slice(0, lastSeparatorIndex);
  const directionSeparatorIndex = presetAndDirection.lastIndexOf(":");
  if (directionSeparatorIndex === -1) {
    return { presetName, phase };
  }

  const direction = presetAndDirection.slice(
    directionSeparatorIndex + 1
  ) as TimelineMotionPresetDirection;
  if (!TIMELINE_MOTION_PRESET_DIRECTIONS.includes(direction)) {
    return { presetName, phase };
  }

  return {
    presetName: presetAndDirection.slice(0, directionSeparatorIndex),
    phase,
    direction,
  };
}

function buildTextStyleMenuKey(
  propertyKey: TextStyleAnimationProperty
): string {
  return `${TEXT_STYLE_MENU_KEY_PREFIX}${propertyKey}`;
}

function parseTextStyleMenuKey(key: string): TextStyleAnimationProperty | null {
  if (!key.startsWith(TEXT_STYLE_MENU_KEY_PREFIX)) {
    return null;
  }

  const propertyKey = key.slice(
    TEXT_STYLE_MENU_KEY_PREFIX.length
  ) as TextStyleAnimationProperty;
  return propertyKey in TEXT_STYLE_ANIMATION_CONFIGS ? propertyKey : null;
}

function buildLayerStyleMenuKey(
  propertyKey: LayerStyleAnimationProperty
): string {
  return `${LAYER_STYLE_MENU_KEY_PREFIX}${propertyKey}`;
}

function parseLayerStyleMenuKey(
  key: string
): LayerStyleAnimationProperty | null {
  if (!key.startsWith(LAYER_STYLE_MENU_KEY_PREFIX)) {
    return null;
  }

  const propertyKey = key.slice(
    LAYER_STYLE_MENU_KEY_PREFIX.length
  ) as LayerStyleAnimationProperty;
  return propertyKey in LAYER_STYLE_MENU_LABELS ? propertyKey : null;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function invertColor(color: number[]): number[] {
  if (color.length <= 3) {
    return color.slice(0, 3).map((entry) => clamp01(1 - entry));
  }

  return [
    ...color.slice(0, 3).map((entry) => clamp01(1 - entry)),
    ...color.slice(3),
  ];
}

function parseHexColor(color: string): number[] | undefined {
  const normalized = color.trim().replace(/^#/, "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((component) => `${component}${component}`)
          .join("")
      : normalized;

  if (!/^[\da-f]{6}$/i.test(expanded)) {
    return undefined;
  }

  return [0, 2, 4].map(
    (index) => parseInt(expanded.slice(index, index + 2), 16) / 255
  );
}

function isBindableMotionProperty(
  value: unknown
): value is {
  a?: number;
  k: unknown;
} {
  return (
    value !== null &&
    typeof value === "object" &&
    "k" in (value as Record<string, unknown>)
  );
}

function normalizeStyleName(value: string): string {
  return value.trim().replace(/\s+/g, " ").toLowerCase();
}

function appendLayerStyleTarget(
  targets: LayerStyleAnimationTarget[],
  propertyKey: Exclude<LayerStyleAnimationProperty, "blur">,
  path: string,
  value: unknown
) {
  if (!isBindableMotionProperty(value)) {
    return;
  }

  targets.push({ kind: "existing", propertyKey, path });
}

function collectShapeLayerStyleTargets(
  items: unknown,
  basePath: string,
  targets: LayerStyleAnimationTarget[]
) {
  if (!Array.isArray(items)) {
    return;
  }

  items.forEach((item, index) => {
    if (!item || typeof item !== "object") {
      return;
    }

    const itemRecord = item as Record<string, unknown>;
    const itemPath = `${basePath}.${index}`;

    if (itemRecord.ty === "fl") {
      appendLayerStyleTarget(
        targets,
        "fillColor",
        `${itemPath}.c`,
        itemRecord.c
      );
      appendLayerStyleTarget(
        targets,
        "fillOpacity",
        `${itemPath}.o`,
        itemRecord.o
      );
    }

    if (itemRecord.ty === "st") {
      appendLayerStyleTarget(
        targets,
        "strokeColor",
        `${itemPath}.c`,
        itemRecord.c
      );
      appendLayerStyleTarget(
        targets,
        "strokeOpacity",
        `${itemPath}.o`,
        itemRecord.o
      );
      appendLayerStyleTarget(
        targets,
        "strokeWidth",
        `${itemPath}.w`,
        itemRecord.w
      );
    }

    if (Array.isArray(itemRecord.it)) {
      collectShapeLayerStyleTargets(itemRecord.it, `${itemPath}.it`, targets);
    }
  });
}

function collectLayerStyleArrayTargets(
  styles: unknown,
  basePath: string,
  targets: LayerStyleAnimationTarget[]
) {
  if (!Array.isArray(styles)) {
    return;
  }

  styles.forEach((style, index) => {
    if (!style || typeof style !== "object") {
      return;
    }

    const styleRecord = style as Record<string, unknown>;
    const stylePath = `${basePath}.${index}`;

    if (styleRecord.ty === 7) {
      appendLayerStyleTarget(
        targets,
        "fillColor",
        `${stylePath}.c`,
        styleRecord.c
      );
      appendLayerStyleTarget(
        targets,
        "fillOpacity",
        `${stylePath}.so`,
        styleRecord.so
      );
    }

    if (styleRecord.ty === 0) {
      appendLayerStyleTarget(
        targets,
        "strokeColor",
        `${stylePath}.c`,
        styleRecord.c
      );
      appendLayerStyleTarget(
        targets,
        "strokeWidth",
        `${stylePath}.s`,
        styleRecord.s
      );
    }
  });
}

function collectEffectLayerStyleTargets(
  effects: unknown,
  basePath: string,
  targets: LayerStyleAnimationTarget[]
) {
  if (!Array.isArray(effects)) {
    return;
  }

  effects.forEach((effect, index) => {
    if (!effect || typeof effect !== "object") {
      return;
    }

    const effectRecord = effect as Record<string, unknown>;
    const effectValues = Array.isArray(effectRecord.ef)
      ? effectRecord.ef
      : undefined;
    if (!effectValues) {
      return;
    }

    const effectPath = `${basePath}.${index}`;
    if (effectRecord.ty === 21) {
      appendLayerStyleTarget(
        targets,
        "fillColor",
        `${effectPath}.ef.2.v`,
        (effectValues[2] as Record<string, unknown> | undefined)?.v
      );
      appendLayerStyleTarget(
        targets,
        "fillOpacity",
        `${effectPath}.ef.6.v`,
        (effectValues[6] as Record<string, unknown> | undefined)?.v
      );
    }

    if (effectRecord.ty === 22) {
      appendLayerStyleTarget(
        targets,
        "strokeColor",
        `${effectPath}.ef.3.v`,
        (effectValues[3] as Record<string, unknown> | undefined)?.v
      );
      appendLayerStyleTarget(
        targets,
        "strokeWidth",
        `${effectPath}.ef.4.v`,
        (effectValues[4] as Record<string, unknown> | undefined)?.v
      );
      appendLayerStyleTarget(
        targets,
        "strokeOpacity",
        `${effectPath}.ef.6.v`,
        (effectValues[6] as Record<string, unknown> | undefined)?.v
      );
    }
  });
}

function collectNamedLayerStyleTargets(
  value: unknown,
  basePath: string,
  targets: LayerStyleAnimationTarget[]
) {
  if (Array.isArray(value)) {
    value.forEach((entry, index) => {
      collectNamedLayerStyleTargets(entry, `${basePath}.${index}`, targets);
    });
    return;
  }

  if (!value || typeof value !== "object") {
    return;
  }

  const record = value as Record<string, unknown>;
  if (typeof record.nm === "string") {
    const propertyKey =
      NAMED_LAYER_STYLE_PROPERTIES[normalizeStyleName(record.nm)];
    if (propertyKey) {
      appendLayerStyleTarget(targets, propertyKey, `${basePath}.v`, record.v);
    }
  }

  Object.entries(record).forEach(([key, entry]) => {
    collectNamedLayerStyleTargets(
      entry,
      basePath ? `${basePath}.${key}` : key,
      targets
    );
  });
}

function collectLayerStyleAnimationTargets(
  layerData: unknown
): Map<
  Exclude<LayerStyleAnimationProperty, "blur">,
  LayerStyleAnimationTarget[]
> {
  const targets: LayerStyleAnimationTarget[] = [];
  const layerRecord = layerData as {
    ty?: number;
    sc?: unknown;
    shapes?: unknown;
    sy?: unknown;
    ef?: unknown;
  };

  collectShapeLayerStyleTargets(layerRecord.shapes, "shapes", targets);
  collectLayerStyleArrayTargets(layerRecord.sy, "sy", targets);
  collectEffectLayerStyleTargets(layerRecord.ef, "ef", targets);
  collectNamedLayerStyleTargets(layerRecord.ef, "ef", targets);

  if (
    layerRecord.ty === 1 &&
    typeof layerRecord.sc === "string" &&
    !targets.some((target) => target.propertyKey === "fillColor")
  ) {
    targets.push({
      kind: "solidFillColor",
      propertyKey: "fillColor",
      color: layerRecord.sc,
    });
  }

  const deduped = new Map<
    Exclude<LayerStyleAnimationProperty, "blur">,
    LayerStyleAnimationTarget[]
  >();

  targets.forEach((target) => {
    const existing = deduped.get(target.propertyKey) ?? [];
    if (
      existing.some((entry) =>
        entry.kind === "existing" && target.kind === "existing"
          ? entry.path === target.path
          : entry.kind === target.kind
      )
    ) {
      return;
    }

    existing.push(target);
    deduped.set(target.propertyKey, existing);
  });

  return deduped;
}

function createScalarProperty(value: number) {
  return {
    a: 0 as const,
    k: value,
  };
}

function createVectorProperty(value: number[]) {
  return {
    a: 0 as const,
    k: [...value],
  };
}

function createFullTextRangeSelector() {
  return {
    t: 0,
    xe: createScalarProperty(0),
    ne: createScalarProperty(0),
    a: createScalarProperty(100),
    b: 1,
    rn: 0,
    sh: 2,
    r: 1,
    sm: createScalarProperty(100),
    o: createScalarProperty(0),
    s: createScalarProperty(0),
    e: createScalarProperty(100),
  };
}

function isAnimatedTextDocument(
  value: unknown
): value is {
  k: Array<{ s: Record<string, unknown>; t: number }>;
} {
  return (
    value !== null &&
    typeof value === "object" &&
    "k" in (value as Record<string, unknown>) &&
    Array.isArray((value as { k?: unknown }).k)
  );
}

function getTextDocumentAtFrame(layer: unknown, localFrame: number) {
  const documentData = (layer as { t?: { d?: unknown } })?.t?.d;
  if (!isAnimatedTextDocument(documentData) || !documentData.k.length) {
    return undefined;
  }

  const sortedKeyframes = [...documentData.k].sort(
    (left, right) => left.t - right.t
  );
  return (
    [...sortedKeyframes].reverse().find((keyframe) => keyframe.t <= localFrame)
      ?.s ?? sortedKeyframes[0]?.s
  );
}

function getTextDocumentColorValue(
  value: unknown,
  fallback: number[]
): number[] {
  if (!Array.isArray(value)) {
    return [...fallback];
  }

  const components = value
    .slice(0, 3)
    .map((component) => (typeof component === "number" ? component : 0));

  return components.length === 3 ? components : [...fallback];
}

function getTextStyleBaseValue(
  layer: unknown,
  propertyKey: TextStyleAnimationProperty,
  localFrame: number
): number | number[] {
  const textDocument = getTextDocumentAtFrame(layer, localFrame);
  const fillColor = getTextDocumentColorValue(textDocument?.fc, [1, 1, 1]);
  const strokeColor = getTextDocumentColorValue(textDocument?.sc, fillColor);

  switch (propertyKey) {
    case "fc":
      return fillColor;
    case "sc":
      return strokeColor;
    case "sw":
      return typeof textDocument?.sw === "number" ? textDocument.sw : 0;
    case "fo":
    case "so":
      return 100;
    default:
      return 0;
  }
}

function getTextStyleTargetValue(
  propertyKey: TextStyleAnimationProperty,
  baseValue: number | number[]
): number | number[] {
  switch (propertyKey) {
    case "bl":
      return 12;
    case "fh":
    case "sh":
      return 90;
    case "fs":
    case "ss":
      return 0.45;
    case "fb":
      return Array.isArray(baseValue) && baseValue[0] > 0.6 ? -0.35 : 0.35;
    case "sb":
      return Array.isArray(baseValue) && baseValue[0] > 0.6 ? -0.35 : 0.35;
    case "fc":
    case "sc":
      return Array.isArray(baseValue) ? invertColor(baseValue) : [0.2, 0.6, 1];
    case "fo":
    case "so":
      return 0;
    case "sw":
      return Math.max(4, typeof baseValue === "number" ? baseValue + 2 : 4);
    default:
      return baseValue;
  }
}

function getResolvedPropertyValueAtFrame(
  layer: unknown,
  path: string,
  localFrame: number
): number | number[] | undefined {
  const rawValue = getValueAtObjectPath(layer, path);
  const collapsedValue = collapseAnimatedPropertiesToStaticValue(
    rawValue,
    localFrame
  );

  if (typeof collapsedValue === "number" && Number.isFinite(collapsedValue)) {
    return collapsedValue;
  }

  if (
    collapsedValue &&
    typeof collapsedValue === "object" &&
    "a" in (collapsedValue as Record<string, unknown>) &&
    "k" in (collapsedValue as Record<string, unknown>) &&
    (collapsedValue as { a?: number }).a === 0
  ) {
    const staticValue = (collapsedValue as { k: unknown }).k;
    if (typeof staticValue === "number" && Number.isFinite(staticValue)) {
      return staticValue;
    }
    if (
      Array.isArray(staticValue) &&
      staticValue.every(
        (entry) => typeof entry === "number" && Number.isFinite(entry)
      )
    ) {
      return staticValue;
    }
  }

  if (
    Array.isArray(collapsedValue) &&
    collapsedValue.every(
      (entry) => typeof entry === "number" && Number.isFinite(entry)
    )
  ) {
    return collapsedValue;
  }

  return undefined;
}

function buildLayerStyleTargetValue(
  propertyKey: LayerStyleAnimationProperty,
  baseValue?: number | number[]
): {
  toValue?: number | number[];
  deltaValue?: number | number[];
} {
  switch (propertyKey) {
    case "blur":
      return { deltaValue: 12 };
    case "hue":
      return {
        deltaValue:
          typeof baseValue === "number" && Math.abs(baseValue) <= 1 ? 0.25 : 90,
      };
    case "saturation":
      return {
        deltaValue:
          typeof baseValue === "number" && Math.abs(baseValue) <= 1 ? 0.35 : 35,
      };
    case "brightness":
      return {
        deltaValue:
          typeof baseValue === "number" && Math.abs(baseValue) <= 1
            ? baseValue > 0.6
              ? -0.35
              : 0.35
            : 35,
      };
    case "fillColor":
    case "strokeColor":
      return {
        toValue: Array.isArray(baseValue)
          ? invertColor(baseValue)
          : [0.2, 0.6, 1],
      };
    case "fillOpacity":
    case "strokeOpacity":
      return { toValue: 0 };
    case "strokeWidth":
      return {
        toValue: typeof baseValue === "number" ? Math.max(4, baseValue + 2) : 4,
      };
  }
}

function buildLayerStyleAnimationTiming(args: {
  currentFrame: number;
  layerTiming: ReturnType<typeof getResolvedLayerTiming>;
}) {
  const { currentFrame, layerTiming } = args;
  const boundedCurrentFrame = Math.max(
    layerTiming.ip,
    Math.min(layerTiming.op, currentFrame)
  );
  const preferredAfterFrame = boundedCurrentFrame + 100;
  const preferredBeforeFrame = boundedCurrentFrame - 100;
  const useAfter =
    preferredAfterFrame <= layerTiming.op ||
    preferredBeforeFrame < layerTiming.ip;
  const otherFrame = useAfter
    ? Math.min(layerTiming.op, preferredAfterFrame)
    : Math.max(layerTiming.ip, preferredBeforeFrame);

  if (otherFrame === boundedCurrentFrame) {
    return null;
  }

  return {
    // Style adds should settle back to the authored layer value on the later keyframe.
    phase: "in",
    timing: {
      startFrame:
        Math.min(boundedCurrentFrame, otherFrame) - layerTiming.timelineOffset,
      endFrame:
        Math.max(boundedCurrentFrame, otherFrame) - layerTiming.timelineOffset,
    },
  } as const;
}

function ensureSolidFillColorEffect(
  layer: unknown
): {
  path: string;
  color: number[];
} | null {
  if (!layer || typeof layer !== "object") {
    return null;
  }

  const layerRecord = layer as {
    ef?: Array<Record<string, unknown>>;
    sc?: unknown;
  };
  const color =
    typeof layerRecord.sc === "string"
      ? parseHexColor(layerRecord.sc)
      : undefined;
  if (!color) {
    return null;
  }

  const effects = Array.isArray(layerRecord.ef)
    ? layerRecord.ef
    : (layerRecord.ef = []);
  let effectIndex = effects.findIndex((effect) => effect?.ty === 21);

  if (effectIndex === -1) {
    effects.push({
      ty: 21,
      nm: "Fill",
      np: 7,
      mn: "ADBE Fill",
      ix: effects.length + 1,
      en: 1,
      ef: [
        {
          ty: 10,
          nm: "Layer",
          mn: "ADBE Fill-0001",
          ix: 1,
          v: { a: 0, k: 0, ix: 1 },
        },
        {
          ty: 7,
          nm: "Mask",
          mn: "ADBE Fill-0002",
          ix: 2,
          v: { a: 0, k: 0, ix: 2 },
        },
        {
          ty: 2,
          nm: "Fill Color",
          mn: "ADBE Fill-0003",
          ix: 3,
          v: { a: 0, k: [...color, 1], ix: 3 },
        },
        {
          ty: 7,
          nm: "Invert",
          mn: "ADBE Fill-0004",
          ix: 4,
          v: { a: 0, k: 0, ix: 4 },
        },
        {
          ty: 0,
          nm: "Horizontal Feather",
          mn: "ADBE Fill-0005",
          ix: 5,
          v: { a: 0, k: 0, ix: 5 },
        },
        {
          ty: 0,
          nm: "Vertical Feather",
          mn: "ADBE Fill-0006",
          ix: 6,
          v: { a: 0, k: 0, ix: 6 },
        },
        {
          ty: 0,
          nm: "Fill Opacity",
          mn: "ADBE Fill-0007",
          ix: 7,
          v: { a: 0, k: 1, ix: 7 },
        },
      ],
    });
    effectIndex = effects.length - 1;
  }

  return {
    path: `ef.${effectIndex}.ef.2.v`,
    color,
  };
}

function hasAnimatedTextStyleProperty(
  layerData: unknown,
  propertyKey: TextStyleAnimationProperty
): boolean {
  const animators = (layerData as {
    t?: { a?: Array<{ a?: Record<string, any> }> };
  })?.t?.a;
  if (!Array.isArray(animators)) {
    return false;
  }

  return animators.some((animator) => {
    const property = animator?.a?.[propertyKey];
    return (
      property &&
      typeof property === "object" &&
      "a" in property &&
      Array.isArray((property as { k?: unknown }).k) &&
      (property as { a?: number }).a === 1
    );
  });
}

function ensureTextStyleAnimatorProperty(args: {
  layer: unknown;
  propertyKey: TextStyleAnimationProperty;
  animatorName: string;
  baseValue: number | number[];
}) {
  const { layer, propertyKey, animatorName, baseValue } = args;
  const textLayer = layer as {
    ty?: number;
    t?: {
      a?: Array<{
        nm?: string;
        s?: Record<string, unknown>;
        a?: Record<string, unknown>;
      }>;
    };
  };
  if (textLayer.ty !== 5 || !textLayer.t) {
    return null;
  }

  textLayer.t.a = Array.isArray(textLayer.t.a) ? textLayer.t.a : [];
  const animatorIndex = textLayer.t.a.length;
  textLayer.t.a.push({
    nm: animatorName,
    s: createFullTextRangeSelector(),
    a: {
      [propertyKey]: Array.isArray(baseValue)
        ? createVectorProperty(baseValue)
        : createScalarProperty(baseValue),
    },
  });

  return `t.a.${animatorIndex}.a.${propertyKey}`;
}

function readTextAnimationKeyframeScalar(value: unknown): number | undefined {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (
    Array.isArray(value) &&
    typeof value[0] === "number" &&
    Number.isFinite(value[0])
  ) {
    return value[0];
  }

  return undefined;
}

function getTextAnimationPhaseTiming(value: unknown): TextAnimationPhaseTiming {
  if (
    !value ||
    typeof value !== "object" ||
    !("a" in (value as Record<string, unknown>)) ||
    !("k" in (value as Record<string, unknown>)) ||
    (value as { a?: number }).a !== 1 ||
    !Array.isArray((value as { k?: unknown }).k)
  ) {
    return {};
  }

  const keyframes = (value as { k: Array<Record<string, unknown>> }).k;
  const timing: TextAnimationPhaseTiming = {};

  keyframes.forEach((keyframe, index) => {
    const fromValue = readTextAnimationKeyframeScalar(keyframe.s);
    const toValue = readTextAnimationKeyframeScalar(keyframe.e);
    const nextKeyframe = keyframes[index + 1];
    const startFrame = typeof keyframe.t === "number" ? keyframe.t : undefined;
    const endFrame =
      typeof nextKeyframe?.t === "number" ? nextKeyframe.t : startFrame;

    if (
      fromValue == null ||
      toValue == null ||
      startFrame == null ||
      endFrame == null ||
      fromValue === toValue
    ) {
      return;
    }

    if (fromValue < toValue) {
      timing.enter = { startFrame, endFrame };
      return;
    }

    timing.exit = { startFrame, endFrame };
  });

  return timing;
}

function buildLayerBoundaryTextAnimationTiming(
  layerTiming: ReturnType<typeof getResolvedLayerTiming>,
  preferredDurationFrames = DEFAULT_TEXT_ANIMATION_DURATION_FRAMES
) {
  const localStart = layerTiming.ip - layerTiming.timelineOffset;
  const localEnd = layerTiming.op - layerTiming.timelineOffset;
  const availableFrames = Math.max(0, localEnd - localStart);
  const singleDuration = Math.min(preferredDurationFrames, availableFrames);
  const splitDuration =
    availableFrames > 0
      ? Math.min(
          preferredDurationFrames,
          Math.max(1, Math.floor(availableFrames / 2))
        )
      : 0;

  return {
    enter: {
      startFrame: localStart,
      endFrame: Math.min(localEnd, localStart + splitDuration),
    },
    exit: {
      startFrame: Math.max(localStart, localEnd - splitDuration),
      endFrame: localEnd,
    },
    singleEnter: {
      startFrame: localStart,
      endFrame: Math.min(localEnd, localStart + singleDuration),
    },
    singleExit: {
      startFrame: Math.max(localStart, localEnd - singleDuration),
      endFrame: localEnd,
    },
  };
}

function buildTextAnimationSelectorTiming({
  phase,
  existingTiming,
  layerTiming,
}: {
  phase: TextAnimationPhase;
  existingTiming: TextAnimationPhaseTiming;
  layerTiming: ReturnType<typeof getResolvedLayerTiming>;
}): TextAnimationSelectorTiming {
  const preferredDuration =
    (existingTiming.enter
      ? existingTiming.enter.endFrame - existingTiming.enter.startFrame
      : undefined) ??
    (existingTiming.exit
      ? existingTiming.exit.endFrame - existingTiming.exit.startFrame
      : undefined) ??
    DEFAULT_TEXT_ANIMATION_DURATION_FRAMES;
  const fallbackTiming = buildLayerBoundaryTextAnimationTiming(
    layerTiming,
    preferredDuration
  );

  if (phase === "in") {
    const enterTiming =
      existingTiming.enter ?? existingTiming.exit ?? fallbackTiming.singleEnter;
    return {
      startFrame: enterTiming.startFrame,
      endFrame: enterTiming.endFrame,
    };
  }

  if (phase === "out") {
    const exitTiming =
      existingTiming.exit ?? existingTiming.enter ?? fallbackTiming.singleExit;
    return {
      startFrame: exitTiming.startFrame,
      endFrame: exitTiming.endFrame,
    };
  }

  const enterTiming = existingTiming.enter ?? fallbackTiming.enter;
  const exitTiming = existingTiming.exit ?? fallbackTiming.exit;

  return {
    startFrame: enterTiming.startFrame,
    endFrame: enterTiming.endFrame,
    exitTiming: {
      startFrame: exitTiming.startFrame,
      endFrame: exitTiming.endFrame,
    },
  };
}

export {
  TEXT_ANIMATION_PHASE_LABELS,
  TEXT_STYLE_ANIMATION_CONFIGS,
  LAYER_STYLE_MENU_LABELS,
  TIMELINE_MOTION_PRESET_DIRECTIONS,
  buildTextPresetMenuKey,
  parseTextPresetMenuKey,
  buildMotionPresetMenuKey,
  parseMotionPresetMenuKey,
  buildTextStyleMenuKey,
  parseTextStyleMenuKey,
  buildLayerStyleMenuKey,
  parseLayerStyleMenuKey,
  getTimelineMotionTarget,
  supportsTimelineAnimation,
  getStaticNumberValue,
  collectLayerStyleAnimationTargets,
  parseHexColor,
  getResolvedPropertyValueAtFrame,
  buildLayerStyleTargetValue,
  buildLayerStyleAnimationTiming,
  ensureSolidFillColorEffect,
  hasAnimatedTextStyleProperty,
  ensureTextStyleAnimatorProperty,
  getTextStyleBaseValue,
  getTextStyleTargetValue,
  getTextAnimationPhaseTiming,
  buildTextAnimationSelectorTiming,
};

export type {
  LayerStyleAnimationProperty,
  LayerStyleAnimationTarget,
  TextStyleAnimationProperty,
  TimelineMotionPresetDirection,
};
